import { ArrowLeft, ArrowRight, Check } from '@/shared/icons/phosphor'
import {
  type Component,
  Index,
  type JSX,
  Match,
  Show,
  Switch,
  children,
  createEffect,
  createMemo,
  createSignal,
  createUniqueId,
  on,
  onCleanup,
  onMount,
} from 'solid-js'
import { Button } from '../../primitives/Button'
import { ChoiceCard } from '../../primitives/ChoiceCard'
import { FIELD_MESSAGE_SLOT, FieldError } from '../../primitives/FieldError'
import { Input } from '../../primitives/Input'
import { Kbd } from '../../primitives/Kbd'
import { RichSelect } from '../RichSelect'
import { trimGoSpace } from './goText'
import { isLikelyEmail } from './isLikelyEmail'
import { isLikelyUrl } from './isLikelyUrl'

export interface QuestionFlowOption {
  value: string
  label: string
  description?: string
}

export interface QuestionFlowField {
  name: string
  type: 'text' | 'email' | 'url'
  label: string
  placeholder?: string
  required?: boolean
  autocomplete?: string
  maxLength?: number
}

export type QuestionFlowStep =
  | { id: string; kind: 'fields'; title: string; description?: string; fields: QuestionFlowField[] }
  | {
      id: string
      kind: 'choice'
      title: string
      description?: string
      name: string
      options: QuestionFlowOption[]
      required?: boolean
    }
  | {
      id: string
      kind: 'select'
      title: string
      description?: string
      name: string
      options: QuestionFlowOption[]
      placeholder?: string
      searchPlaceholder?: string
      required?: boolean
    }

export interface QuestionFlowLabels {
  next: string
  back: string
  submit: string
  counter: (current: number, total: number) => string
  enterHint?: string
}

export interface QuestionFlowErrors {
  required: string
  email: string
  url: string
}

export interface QuestionFlowProps {
  steps: QuestionFlowStep[]
  values: Record<string, string>
  onChange: (name: string, value: string) => void
  onComplete: (values: Record<string, string>) => void
  labels: QuestionFlowLabels
  errors: QuestionFlowErrors
  onStepViewed?: (stepId: string, index: number) => void
  initialStep?: number
  submitting?: boolean
  footer?: JSX.Element
  class?: string
}

export const QUESTION_FLOW_AUTO_ADVANCE_MS = 400

const CHOICE_KEYS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
const STEP_PROBLEM = '__step'
const NON_TEXT_INPUT_TYPES = new Set(['radio', 'range', 'checkbox'])

function fieldProblem(
  field: QuestionFlowField,
  raw: string | undefined,
  errors: QuestionFlowErrors
): string | null {
  const value = trimGoSpace(raw ?? '')
  if (value === '') {
    return field.required === false ? null : errors.required
  }
  if (field.type === 'email' && !isLikelyEmail(value)) {
    return errors.email
  }
  if (field.type === 'url' && !isLikelyUrl(value)) {
    return errors.url
  }
  return null
}

function hasFinePointer(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(pointer: fine)').matches
  )
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false
  }
  if (target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) {
    return true
  }
  if (target.closest('[contenteditable]:not([contenteditable="false"])')) {
    return true
  }
  return target instanceof HTMLInputElement && !NON_TEXT_INPUT_TYPES.has(target.type)
}

function isActivationTarget(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest('button, a[href], [role="button"]') !== null
}

function isImeComposing(event: KeyboardEvent): boolean {
  return event.isComposing || event.keyCode === 229
}

export const QuestionFlow: Component<QuestionFlowProps> = (props) => {
  const flowId = createUniqueId()
  const problemId = `${flowId}-problem`
  const counterId = `${flowId}-counter`
  const finePointer = hasFinePointer()
  const total = () => props.steps.length
  const clampStep = (n: number) => Math.min(Math.max(n, 0), Math.max(props.steps.length - 1, 0))

  const [index, setIndex] = createSignal(clampStep(props.initialStep ?? 0))
  const [direction, setDirection] = createSignal<'forward' | 'back'>('forward')
  const [problems, setProblems] = createSignal<Record<string, string>>({})

  let rootRef: HTMLDivElement | undefined
  let headingRef: HTMLHeadingElement | undefined
  let autoAdvanceTimer: ReturnType<typeof setTimeout> | undefined
  let pointerPickPending = false

  const current = createMemo(() => props.steps[clampStep(index())])
  const stepId = createMemo(() => current()?.id)
  const fieldsStep = createMemo(() => {
    const step = current()
    return step?.kind === 'fields' ? step : undefined
  })
  const choiceStep = createMemo(() => {
    const step = current()
    return step?.kind === 'choice' ? step : undefined
  })
  const selectStep = createMemo(() => {
    const step = current()
    return step?.kind === 'select' ? step : undefined
  })
  const footer = children(() => props.footer)
  const isLast = () => index() >= total() - 1
  const submittingLast = () => isLast() && props.submitting === true
  const answerOf = (name: string) => props.values[name] ?? ''
  const headingId = (id: string) => `${flowId}-${id}-title`

  const clearAutoAdvance = () => {
    if (autoAdvanceTimer !== undefined) {
      clearTimeout(autoAdvanceTimer)
      autoAdvanceTimer = undefined
    }
  }

  const setValue = (name: string, value: string) => {
    props.onChange(name, value)
    setProblems((previous) => {
      if (!(name in previous) && !(STEP_PROBLEM in previous)) {
        return previous
      }
      const next = { ...previous }
      delete next[name]
      delete next[STEP_PROBLEM]
      return next
    })
  }

  const goTo = (next: number, nextDirection: 'forward' | 'back') => {
    clearAutoAdvance()
    setProblems({})
    setDirection(nextDirection)
    setIndex(clampStep(next))
  }

  const validate = (step: QuestionFlowStep): Record<string, string> => {
    if (step.kind === 'fields') {
      const found: Record<string, string> = {}
      for (const field of step.fields) {
        const message = fieldProblem(field, props.values[field.name], props.errors)
        if (message) {
          found[field.name] = message
        }
      }
      return found
    }
    if (step.required !== false && trimGoSpace(answerOf(step.name)) === '') {
      return { [STEP_PROBLEM]: props.errors.required }
    }
    return {}
  }

  const confirm = () => {
    const step = current()
    if (!step) {
      return
    }
    const found = validate(step)
    const firstProblem = Object.keys(found)[0]
    if (firstProblem) {
      clearAutoAdvance()
      setProblems(found)
      if (firstProblem !== STEP_PROBLEM) {
        rootRef?.querySelector<HTMLInputElement>(`input[name="${firstProblem}"]`)?.focus()
      }
      return
    }
    if (isLast()) {
      clearAutoAdvance()
      if (!props.submitting) {
        props.onComplete({ ...props.values })
      }
      return
    }
    goTo(index() + 1, 'forward')
  }

  const confirmFromClick = (event: MouseEvent) => {
    if (event.detail > 1) {
      return
    }
    confirm()
  }

  const scheduleAutoAdvance = () => {
    clearAutoAdvance()
    if (isLast()) {
      return
    }
    const scheduled = index()
    autoAdvanceTimer = setTimeout(() => {
      autoAdvanceTimer = undefined
      const step = props.steps[scheduled]
      if (step && index() === scheduled && Object.keys(validate(step)).length === 0) {
        goTo(scheduled + 1, 'forward')
      }
    }, QUESTION_FLOW_AUTO_ADVANCE_MS)
  }

  const recordPointerIntent = (event: PointerEvent) => {
    const target = event.target
    pointerPickPending =
      target instanceof Element &&
      rootRef?.contains(target) === true &&
      target.closest('[data-choice-card]') !== null
  }

  const forgetPointerIntent = () => {
    pointerPickPending = false
  }

  const pick = (name: string, value: string, event: MouseEvent) => {
    const fromPointer = pointerPickPending || event.detail >= 1
    pointerPickPending = false
    setValue(name, value)
    if (fromPointer) {
      scheduleAutoAdvance()
    } else {
      clearAutoAdvance()
    }
  }

  const focusRadio = (value: string) => {
    const radios = rootRef?.querySelectorAll<HTMLInputElement>(
      '[role="radiogroup"] input[type="radio"]'
    )
    Array.from(radios ?? [])
      .find((radio) => radio.value === value)
      ?.focus()
  }

  const handleFieldKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'Enter' || event.repeat || isImeComposing(event)) {
      return
    }
    event.preventDefault()
    confirm()
  }

  const handleDocumentKeyDown = (event: KeyboardEvent) => {
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey || event.repeat) {
      return
    }
    const target = event.target
    if (isEditableTarget(target)) {
      return
    }
    const insideFlow = target instanceof Node && rootRef?.contains(target) === true
    if (target !== document.body && !insideFlow) {
      return
    }
    const step = current()
    if (!step) {
      return
    }
    if (event.key === 'Enter') {
      if (isImeComposing(event) || isActivationTarget(target)) {
        return
      }
      event.preventDefault()
      confirm()
      return
    }
    if (step.kind !== 'choice' || event.key.length !== 1) {
      return
    }
    const option = step.options[CHOICE_KEYS.indexOf(event.key.toUpperCase())]
    if (!option) {
      return
    }
    event.preventDefault()
    setValue(step.name, option.value)
    focusRadio(option.value)
    scheduleAutoAdvance()
  }

  createEffect(
    on(index, (i) => {
      const step = props.steps[i]
      if (step) {
        props.onStepViewed?.(step.id, i)
      }
    })
  )

  createEffect(
    on(
      stepId,
      (id) => {
        if (id === undefined) {
          return
        }
        queueMicrotask(() => {
          if (finePointer && current()?.kind === 'fields') {
            const first = rootRef?.querySelector<HTMLInputElement>(
              '[data-question-flow-fields] input'
            )
            if (first) {
              first.focus()
              return
            }
          }
          headingRef?.focus()
        })
      },
      { defer: true }
    )
  )

  onMount(() => {
    document.addEventListener('pointerdown', recordPointerIntent, true)
    document.addEventListener('pointercancel', forgetPointerIntent, true)
    document.addEventListener('keydown', forgetPointerIntent, true)
    document.addEventListener('keydown', handleDocumentKeyDown)
  })

  onCleanup(() => {
    document.removeEventListener('pointerdown', recordPointerIntent, true)
    document.removeEventListener('pointercancel', forgetPointerIntent, true)
    document.removeEventListener('keydown', forgetPointerIntent, true)
    document.removeEventListener('keydown', handleDocumentKeyDown)
    clearAutoAdvance()
  })

  const stepProblem = () => (
    <div class={FIELD_MESSAGE_SLOT}>
      <Show when={problems()[STEP_PROBLEM]}>
        {(message) => (
          <FieldError id={problemId} role="alert">
            {message()}
          </FieldError>
        )}
      </Show>
    </div>
  )

  const firstFieldProblem = () => {
    const entries = Object.entries(problems()).filter(([name]) => name !== STEP_PROBLEM)
    return entries[0]?.[1]
  }

  return (
    <div
      ref={rootRef}
      data-question-flow
      class={`mx-auto flex w-full max-w-xl flex-col ${props.class ?? ''}`}
    >
      <div class="mb-6 flex min-h-11 items-center justify-between gap-3">
        <Show when={index() > 0} fallback={<span aria-hidden="true" />}>
          <Button
            variant="ghost"
            size="sm"
            class="min-h-11 min-w-11"
            onClick={() => goTo(index() - 1, 'back')}
            aria-label={props.labels.back}
          >
            <ArrowLeft class="h-4 w-4" aria-hidden="true" />
          </Button>
        </Show>
        <p
          id={counterId}
          class="text-[length:var(--text-sm)] font-[var(--font-medium)] tabular-nums text-[color:var(--color-text-muted)]"
        >
          {props.labels.counter(index() + 1, total())}
        </p>
      </div>

      <Show when={stepId()} keyed>
        {(id) => (
          <div
            data-question-flow-step={id}
            class={direction() === 'back' ? 'animate-slide-down' : 'animate-slide-up'}
          >
            <div class="mb-6 flex flex-col gap-2">
              <h2
                ref={headingRef}
                id={headingId(id)}
                aria-describedby={counterId}
                tabIndex={-1}
                class="break-words text-balance font-[family-name:var(--font-display)] text-[length:var(--text-xl)] sm:text-[length:var(--text-2xl)] font-[var(--font-semibold)] leading-[var(--leading-tight)] tracking-[var(--tracking-tight)] text-[color:var(--color-text)] outline-none"
              >
                {current()?.title}
              </h2>
              <Show when={current()?.description}>
                {(description) => (
                  <p class="text-[length:var(--text-base)] text-[color:var(--color-text-muted)]">
                    {description()}
                  </p>
                )}
              </Show>
            </div>

            <Switch>
              <Match when={fieldsStep()}>
                {(step) => (
                  <div
                    data-question-flow-fields
                    class="flex flex-col gap-4"
                    onKeyDown={handleFieldKeyDown}
                  >
                    <Index each={step().fields}>
                      {(field) => (
                        <Input
                          name={field().name}
                          type={field().type}
                          size="lg"
                          label={field().label}
                          optional={field().required === false}
                          placeholder={field().placeholder}
                          autocomplete={field().autocomplete}
                          maxLength={field().maxLength}
                          inputMode={field().type === 'text' ? undefined : field().type}
                          aria-required={field().required !== false}
                          value={answerOf(field().name)}
                          error={problems()[field().name] ?? ''}
                          onInput={(event) => setValue(field().name, event.currentTarget.value)}
                        />
                      )}
                    </Index>
                    <Show when={firstFieldProblem()}>
                      {(message) => (
                        <p role="alert" class="sr-only">
                          {message()}
                        </p>
                      )}
                    </Show>
                  </div>
                )}
              </Match>
              <Match when={choiceStep()}>
                {(step) => (
                  <div class="flex flex-col gap-1.5">
                    <div
                      role="radiogroup"
                      aria-labelledby={headingId(id)}
                      aria-required={step().required !== false}
                      aria-describedby={problems()[STEP_PROBLEM] ? problemId : undefined}
                      class="flex flex-col gap-2.5"
                    >
                      <Index each={step().options}>
                        {(option, optionIndex) => (
                          <ChoiceCard
                            name={`${flowId}-${step().name}`}
                            value={option().value}
                            label={
                              <span class="flex flex-col">
                                <span>{option().label}</span>
                                <Show when={option().description}>
                                  <span class="text-[length:var(--text-sm)] text-[color:var(--color-text-muted)]">
                                    {option().description}
                                  </span>
                                </Show>
                              </span>
                            }
                            hint={finePointer ? CHOICE_KEYS.charAt(optionIndex) : ''}
                            checked={answerOf(step().name) === option().value}
                            onChange={() => setValue(step().name, option().value)}
                            onClick={(event) => pick(step().name, option().value, event)}
                          />
                        )}
                      </Index>
                    </div>
                    {stepProblem()}
                  </div>
                )}
              </Match>
              <Match when={selectStep()}>
                {(step) => (
                  <div class="flex flex-col gap-1.5">
                    <RichSelect
                      value={answerOf(step().name) || undefined}
                      onChange={(value) => setValue(step().name, value)}
                      options={step().options.map((option) =>
                        option.description
                          ? {
                              value: option.value,
                              label: option.label,
                              description: option.description,
                            }
                          : { value: option.value, label: option.label }
                      )}
                      placeholder={step().placeholder ?? ''}
                      searchPlaceholder={step().searchPlaceholder ?? ''}
                      aria-labelledby={headingId(id)}
                      aria-invalid={problems()[STEP_PROBLEM] ? true : undefined}
                      aria-describedby={problems()[STEP_PROBLEM] ? problemId : undefined}
                      size="lg"
                      searchable
                    />
                    {stepProblem()}
                  </div>
                )}
              </Match>
            </Switch>
          </div>
        )}
      </Show>

      <Show when={isLast() && footer()}>
        <div class="mt-6">{footer()}</div>
      </Show>

      <div
        data-question-flow-actions
        class="mt-8 flex flex-wrap items-center justify-end gap-x-4 gap-y-2"
      >
        <Show when={finePointer && props.labels.enterHint}>
          <span
            aria-hidden="true"
            class="hidden items-center gap-1.5 text-[length:var(--text-sm)] text-[color:var(--color-text-muted)] sm:inline-flex"
          >
            <Kbd>↵</Kbd>
            {props.labels.enterHint}
          </span>
        </Show>
        <Button
          type="button"
          size="lg"
          class="w-full sm:w-auto"
          loading={submittingLast()}
          onClick={confirmFromClick}
        >
          <Show when={isLast() && !submittingLast()}>
            <Check class="h-5 w-5" weight="bold" aria-hidden="true" />
          </Show>
          {isLast() ? props.labels.submit : props.labels.next}
          <Show when={!isLast()}>
            <ArrowRight class="h-5 w-5" weight="bold" aria-hidden="true" />
          </Show>
        </Button>
      </div>
    </div>
  )
}
